import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import {
  layout,
  palette,
  radius,
  shadow,
  spacing,
  status,
  transition,
  typography,
} from '../../../../style-utils/theme';
import { up } from '../../../../style-utils/media-queries';

/**
 * THE HOST GUIDE EDITOR'S SHARED PARTS (plan S7.8). Tokens only; no component
 * selectors (CRA has no emotion babel plugin — entity-gallery.styled.tsx).
 */

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
  padding-bottom: ${layout.fabReserve};
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
`;

export const TopBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
`;

/* The editor's own tab strip: the listing tab strip's look (one moving pill),
   plain Links whose current one is the tab the router matched. */
export const EditorTab = styled(Link)`
  position: relative;
  isolation: isolate;
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: ${layout.touchTarget};
  box-sizing: border-box;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
  text-align: center;
  text-decoration: none;
  transition: ${transition.colors};

  &:hover {
    color: ${palette.ink};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &[aria-current='page'] {
    color: ${palette.ink};
  }
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};

  ${up('md')} {
    padding: ${spacing.md};
  }
`;

export const SectionHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const H2 = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;

export const H3 = styled.h3`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const Text = styled.p`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

export const Muted = styled.p`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const SaveBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.sm};
  min-width: 0;
`;

export type BannerTone = 'warning' | 'neutral' | 'alert' | 'brand';

/* A notice band from the status triples — text always present, never colour
   alone. The warning tone carries a 3px edge so it reads as one at a glance. */
export const Banner = styled.div<{ $tone: BannerTone }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${({ $tone }) => status[$tone].dot};
  border-left-width: 4px;
  border-radius: ${radius.sm};
  background: ${({ $tone }) => status[$tone].bg};
  color: ${({ $tone }) => status[$tone].fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  overflow-wrap: anywhere;
`;

export const BannerTitle = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: inherit;
`;

export const BannerText = styled.p`
  margin: 0;
  color: inherit;
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: ${spacing.sm};
  min-width: 0;

  ${up('md')} {
    grid-template-columns: 1fr 1fr;
  }
`;

/* A field that spans both columns of FieldGrid. */
export const Wide = styled.div`
  min-width: 0;

  ${up('md')} {
    grid-column: 1 / -1;
  }
`;

export const StepperField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const StepperLabel = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.inkSoft};
`;

export const List = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: 0;
  padding: 0;
  list-style: none;
  min-width: 0;
`;

export const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
`;

export const ChecklistRow = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;

  & > input {
    flex: 1 1 200px;
    min-width: 0;
  }
`;

export const Counter = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

export const Thumb = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* A visually hidden file input: the styled <label> beside it is the target. */
export const HiddenFile = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

/* The label of a hidden file input, drawn as a secondary button. */
export const FileButton = styled.label<{ $disabled?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.teal};
    background: ${palette.tealTintSoft};
  }

  &:focus-within {
    box-shadow: ${shadow.focus};
  }
`;
